import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Resolve a media URL to an absolute one.
 * Stored relative paths (`/api/uploads/…`, `/api/media-library/…`) get the
 * current backend host prepended so the image works regardless of the host
 * the frontend is served from (preview vs production).
 * Handles legacy absolute preview URLs by rewriting the host portion.
 */
export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  // Legacy absolute URL that points at an Emergent preview or old host — rewrite
  const emergentMatch = url.match(/^https?:\/\/[^/]+(\/api\/(?:uploads|media-library|reviews)\/[^\s"]+)$/);
  if (emergentMatch) return `${BACKEND_URL}${emergentMatch[1]}`;
  return url;
};

// SEC-001: cookie-based admin session. `withCredentials` sends the HttpOnly cookie
// on every admin request without JavaScript ever touching the token.
export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const fetchProducts = async (params = {}) => (await api.get("/products", { params })).data;
export const fetchProduct = async (slug) => (await api.get(`/products/${slug}`)).data;
export const fetchReviews = async () => (await api.get("/reviews")).data;
export const fetchFaqs = async () => (await api.get("/faqs")).data;
export const subscribeNewsletter = async (email) => (await api.post("/newsletter", { email })).data;
export const checkPromo = async (code, subtotal, existing_codes = []) => (await api.post("/promo/check", { code, subtotal, existing_codes })).data;
export const createOrder = async (payload) => (await api.post("/orders", payload)).data;
export const createStripeCheckout = async (payload) => (await api.post("/payments/checkout", payload)).data;
export const getPaymentStatus = async (sessionId) => (await api.get(`/payments/status/${sessionId}`)).data;
export const submitContact = async (payload) => (await api.post("/contact", payload)).data;
export const fetchCarriers = async () => (await api.get("/carriers")).data;

// Admin — session cookie handles auth, no header needed
export const adminLogin = async (key) => (await api.post("/admin/login", { key })).data;
export const adminLogout = async () => (await api.post("/admin/logout")).data;
export const adminMe = async () => (await api.get("/admin/me")).data;

export const adminListCarriers = async () => (await api.get("/admin/carriers")).data;
export const adminCreateCarrier = async (data) => (await api.post("/admin/carriers", data)).data;
export const adminUpdateCarrier = async (id, data) => (await api.put(`/admin/carriers/${id}`, data)).data;
export const adminDeleteCarrier = async (id) => (await api.delete(`/admin/carriers/${id}`)).data;

// Admin — Products
export const adminListProducts = async () => (await api.get("/admin/products")).data;
export const adminCreateProduct = async (data) => (await api.post("/admin/products", data)).data;
export const adminUpdateProduct = async (id, data) => (await api.put(`/admin/products/${id}`, data)).data;
export const adminDeleteProduct = async (id) => (await api.delete(`/admin/products/${id}`)).data;
export const adminUploadImage = async (file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post("/admin/upload-image", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
  });
  return res.data;
};

// Media
export const fetchMedia = async (params = {}) => (await api.get("/media", { params })).data;
export const adminListMedia = async () => (await api.get("/admin/media")).data;
export const adminCreateMedia = async (d) => (await api.post("/admin/media", d)).data;
export const adminUpdateMedia = async (id, d) => (await api.put(`/admin/media/${id}`, d)).data;
export const adminDeleteMedia = async (id) => (await api.delete(`/admin/media/${id}`)).data;

// Admin — Orders
export const adminListOrders = async (status) => {
  const params = status ? { status } : {};
  return (await api.get("/admin/orders", { params })).data;
};
export const adminUpdateOrder = async (id, data) =>
  (await api.put(`/admin/orders/${id}`, data)).data;
export const adminResendConfirmation = async (id) =>
  (await api.post(`/admin/orders/${id}/resend-confirmation`)).data;
export const adminGenerateBoxtalLabel = async (id) =>
  (await api.post(`/admin/orders/${id}/boxtal-label`)).data;
export const adminBoxtalStatus = async () =>
  (await api.get("/admin/boxtal/status")).data;
export const adminLabelUrl = (id) => `${API}/admin/orders/${id}/label`;
export const adminSlipUrl = (id) => `${API}/admin/orders/${id}/packing-slip`;
export const adminOpenPdf = async (path) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
